import { Button, Card, Form, FormProps, Row, Space } from 'antd';
import { Dispatch, SetStateAction, useCallback, useEffect, useRef, useState } from 'react';

interface CorporateReportFilterFormProps {
  searchFormProps: FormProps;
  children: React.ReactNode;
  tableLoading?: boolean;
  setHasSearched: Dispatch<SetStateAction<boolean>>;
  onValidateFilter?: () => boolean;
  submitAfterClear?: boolean;
  fallbackMs?: number;
}

export const CorporateReportFilterForm = ({
  searchFormProps,
  children,
  tableLoading,
  setHasSearched,
  onValidateFilter,
  submitAfterClear = false,
  fallbackMs = 1500
}: CorporateReportFilterFormProps) => {
  const [isFiltering, setIsFiltering] = useState(false);
  const isWaitingFilterResultRef = useRef(false);
  const hasFilterRequestStartedRef = useRef(false);
  const filterFallbackTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const stopFilterLoading = useCallback(() => {
    setIsFiltering(false);
    isWaitingFilterResultRef.current = false;
    hasFilterRequestStartedRef.current = false;

    if (filterFallbackTimeoutRef.current) {
      clearTimeout(filterFallbackTimeoutRef.current);
      filterFallbackTimeoutRef.current = null;
    }
  }, []);

  const startFilterLoading = useCallback(() => {
    setIsFiltering(true);
    isWaitingFilterResultRef.current = true;
    hasFilterRequestStartedRef.current = false;

    if (filterFallbackTimeoutRef.current) {
      clearTimeout(filterFallbackTimeoutRef.current);
    }

    filterFallbackTimeoutRef.current = setTimeout(() => {
      if (
        isWaitingFilterResultRef.current &&
        !hasFilterRequestStartedRef.current
      ) {
        stopFilterLoading();
      }
    }, fallbackMs);
  }, [fallbackMs, stopFilterLoading]);

  useEffect(() => {
    if (!isWaitingFilterResultRef.current) {
      return;
    }

    if (tableLoading) {
      hasFilterRequestStartedRef.current = true;
      return;
    }

    if (hasFilterRequestStartedRef.current) {
      stopFilterLoading();
    }
  }, [tableLoading, stopFilterLoading]);

  useEffect(
    () => () => {
      if (filterFallbackTimeoutRef.current) {
        clearTimeout(filterFallbackTimeoutRef.current);
      }
    },
    []
  );

  const handleClear = () => {
    searchFormProps.form?.resetFields();
    setHasSearched(false);
    stopFilterLoading();

    if (submitAfterClear) {
      searchFormProps.form?.submit();
    }
  };

  const handleFilterClick = (event: React.MouseEvent<HTMLElement>) => {
    if (onValidateFilter && !onValidateFilter()) {
      event.preventDefault();
      stopFilterLoading();
    }
  };

  return (
    <Card className="filter-form-card p-2 mb-3" size="small">
      <Form
        {...searchFormProps}
        layout="horizontal"
        onFinish={async values => {
          startFilterLoading();
          await Promise.resolve(searchFormProps.onFinish?.(values));
          setHasSearched(true);
        }}
      >
        <Row
          align="middle"
          justify="start"
          className="gap-4 [&>.ant-form-item]:mb-0 [&>.ant-form-item]:w-full sm:[&>.ant-form-item]:w-auto"
        >
          {children}
          <Space className="ml-auto w-full justify-end pt-1 sm:w-auto sm:pt-0">
            <Button type="default" onClick={handleClear}>
              Limpar filtro
            </Button>
            <Button
              type="primary"
              htmlType="submit"
              ghost
              disabled={isFiltering}
              onClick={handleFilterClick}
            >
              Filtrar
            </Button>
          </Space>
        </Row>
      </Form>
    </Card>
  );
};
